import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Grid,
  Tab,
  Tabs,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Visibility,
  LocalOffer,
  Schedule,
  People,
  EmojiEvents,
  Search,
  Business,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { collection, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { VenueData, OfferData } from '../../types/VenueAdminTypes';

// ─── Props ────────────────────────────────────────────────────────────────────

interface VenueAdminAnalyticsProps {
  adminId: string;
  venues: VenueData[];
  offers: OfferData[];
  stats: {
    totalViews: number;
    totalRedemptions: number;
    monthlyViews: number;
    monthlyRedemptions: number;
  };
}

// ─── Data shapes ──────────────────────────────────────────────────────────────

interface TimeSeriesPoint {
  date: string;
  // venue
  venueSearches: number;
  venueViews: number;
  // offer
  offerViews: number;
  offerRedemptions: number;
}

interface VenuePerformance {
  venueId: string;
  venueName: string;
  searches: number;
  views: number;
  conversionRate: number;
}

interface OfferPerformance {
  id: string;
  title: string;
  type: string;
  views: number;
  redemptions: number;
  conversionRate: number;
  venueName: string;
}

interface HourlyData {
  hour: string;
  searches: number;
}

interface DayOfWeekData {
  day: string;
  searches: number;
  views: number;
}

type TimeRange = 7 | 30 | 60;

// ─── Chart theme ─────────────────────────────────────────────────────────────

const COLORS = ['#6c63ff', '#16a76a', '#f59e0b', '#e040fb', '#3b82f6', '#ef4444', '#14b8a6'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const GRID_STYLE = { stroke: '#eef0f5', strokeDasharray: '4 4' };
const AXIS_STYLE = {
  tick: { fill: '#8a94a6', fontSize: 11 },
  axisLine: { stroke: '#e8eaf0' },
  tickLine: false,
};
const TOOLTIP_STYLE = {
  contentStyle: {
    background: '#fff', border: '1px solid #e8eaf0', borderRadius: 10,
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)', fontSize: 12, padding: '10px 14px',
  },
  labelStyle: { fontWeight: 600, color: '#1a1d23', marginBottom: 4 },
};

// ─── Small helpers ────────────────────────────────────────────────────────────

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <Box mb={2.5}>
      <Typography variant="h6" fontWeight={600} color="#1a1d23">{title}</Typography>
      {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
    </Box>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <Box display="flex" alignItems="center" gap={0.75}>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
      <Typography sx={{ fontSize: '0.75rem', color: '#8a94a6' }}>{label}</Typography>
    </Box>
  );
}

function StatCard({
  label, value, sub, icon, accent, bgTint, trend, trendSuffix,
}: {
  label: string; value: string | number; sub?: string;
  icon: React.ReactNode; accent: string; bgTint: string;
  trend?: number | null; trendSuffix?: string;
}) {
  return (
    <Box sx={{
      background: '#fff', borderRadius: 2.5, p: 2.5,
      border: '1px solid #e8eaf0', borderLeft: `4px solid ${accent}`,
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      transition: 'transform 0.15s, box-shadow 0.15s',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.09)' },
    }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#8a94a6', mb: 0.75 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '1.9rem', fontWeight: 700, color: '#1a1d23', lineHeight: 1, mb: 0.5 }}>
            {value}
          </Typography>
          {trend !== undefined && (
            <Box display="flex" alignItems="center" gap={0.5}>
              {trend === null ? (
                <Typography sx={{ fontSize: '0.72rem', color: '#8a94a6' }}>-- vs prev period</Typography>
              ) : (
                <>
                  {trend >= 0
                    ? <TrendingUp sx={{ fontSize: 13, color: '#16a76a' }} />
                    : <TrendingDown sx={{ fontSize: 13, color: '#ef4444' }} />}
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: trend >= 0 ? '#16a76a' : '#ef4444' }}>
                    {Math.abs(trend)}{trendSuffix || '%'} vs prev period
                  </Typography>
                </>
              )}
            </Box>
          )}
          {sub && !trend && (
            <Typography sx={{ fontSize: '0.75rem', color: '#8a94a6' }}>{sub}</Typography>
          )}
        </Box>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, background: bgTint, color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: 20 } }}>
          {icon}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const VenueAdminAnalytics: React.FC<VenueAdminAnalyticsProps> = ({
  adminId, venues, offers, stats,
}) => {
  const [timeRange, setTimeRange] = useState<TimeRange>(30);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ── Venue analytics state ──
  const [venueTimeSeries, setVenueTimeSeries] = useState<TimeSeriesPoint[]>([]);
  const [venuePerformance, setVenuePerformance] = useState<VenuePerformance[]>([]);
  const [hourlySearchData, setHourlySearchData] = useState<HourlyData[]>([]);
  const [dayOfWeekData, setDayOfWeekData] = useState<DayOfWeekData[]>([]);
  const [periodVenueSearches, setPeriodVenueSearches] = useState(0);
  const [periodVenueViews, setPeriodVenueViews] = useState(0);
  const [venueSearchTrend, setVenueSearchTrend] = useState<number | null>(null);
  const [venueViewTrend, setVenueViewTrend] = useState<number | null>(null);
  const [peakHourRange, setPeakHourRange] = useState('');
  const [peakDayOfWeek, setPeakDayOfWeek] = useState('');
  const [weekendLift, setWeekendLift] = useState(0);

  // ── Offer analytics state ──
  const [offerTimeSeries, setOfferTimeSeries] = useState<TimeSeriesPoint[]>([]);
  const [offerPerformance, setOfferPerformance] = useState<OfferPerformance[]>([]);
  const [periodOfferViews, setPeriodOfferViews] = useState(0);
  const [periodOfferRedemptions, setPeriodOfferRedemptions] = useState(0);
  const [offerViewTrend, setOfferViewTrend] = useState<number | null>(null);
  const [offerRedemptionTrend, setOfferRedemptionTrend] = useState<number | null>(null);
  const [overallConversionRate, setOverallConversionRate] = useState(0);

  useEffect(() => { loadAll(); }, [adminId, timeRange, venues, offers]);

  // ─── Data loading ─────────────────────────────────────────────────────────

  const loadAll = async () => {
    try {
      setLoading(true);
      setError('');
      await Promise.all([
        loadVenueAnalytics().catch(err => {
          console.error('loadVenueAnalytics failed:', err.code, err.message);
          throw err;
        }),
        loadOfferAnalytics().catch(err => {
          console.error('loadOfferAnalytics failed:', err.code, err.message);
          throw err;
        }),
      ]);
    } catch (err) {
      console.error('Analytics load error:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const loadVenueAnalytics = async () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - timeRange);

    const prevEndDate = new Date(startDate);
    prevEndDate.setDate(prevEndDate.getDate() - 1);
    const prevStartDate = new Date(prevEndDate);
    prevStartDate.setDate(prevStartDate.getDate() - timeRange);

    const currentStartStr = startDate.toISOString().substring(0, 10);
    const currentEndStr = endDate.toISOString().substring(0, 10);
    const prevStartStr = prevStartDate.toISOString().substring(0, 10);
    const prevEndStr = prevEndDate.toISOString().substring(0, 10);

    const filteredVenues = venues.filter(v => v.ownerId === adminId && v.ownership?.adminId === adminId);
    const venueIds = filteredVenues.map(v => v.id);
    if (!venueIds.length) return;

    // Fetch current + previous period from analytics_daily
    const [currentSnaps, prevSnaps] = await Promise.all([
      Promise.all(venueIds.map(vId =>
        getDocs(query(
          collection(db, 'analytics_daily'),
          where('venueId', '==', vId),
          where('date', '>=', currentStartStr),
          where('date', '<=', currentEndStr),
          orderBy('date', 'asc'),
        ))
      )),
      Promise.all(venueIds.map(vId =>
        getDocs(query(
          collection(db, 'analytics_daily'),
          where('venueId', '==', vId),
          where('date', '>=', prevStartStr),
          where('date', '<=', prevEndStr),
          orderBy('date', 'asc'),
        ))
      )),
    ]);

    // Build time-series map
    const dailyMap: Record<string, { searches: number; views: number }> = {};
    for (let i = timeRange; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      dailyMap[d.toISOString().substring(0, 10)] = { searches: 0, views: 0 };
    }

    let totalSearches = 0, totalViews = 0;
    const hourlyS = new Array(24).fill(0);
    const dowS = new Array(7).fill(0);
    const dowV = new Array(7).fill(0);

    currentSnaps.forEach(snap => snap.docs.forEach(doc => {
      const d = doc.data();
      const dateStr = d.date as string;
      if (dailyMap[dateStr]) {
        dailyMap[dateStr].searches += d.searches || 0;
        dailyMap[dateStr].views += d.views || 0;
      }
      totalSearches += d.searches || 0;
      totalViews += d.views || 0;
      for (let h = 0; h < 24; h++) hourlyS[h] += d[`hourly_searches_${h}`] || 0;
      for (let dw = 0; dw < 7; dw++) {
        dowS[dw] += d[`dow_searches_${dw}`] || 0;
        dowV[dw] += d[`dow_views_${dw}`] || 0;
      }
    }));

    // Previous period totals for trend calc
    let prevSearches = 0, prevViews = 0;
    prevSnaps.forEach(snap => snap.docs.forEach(doc => {
      const d = doc.data();
      prevSearches += d.searches || 0;
      prevViews += d.views || 0;
    }));

    const calcTrend = (curr: number, prev: number) =>
      prev === 0 ? null : parseFloat((((curr - prev) / prev) * 100).toFixed(1));

    setPeriodVenueSearches(totalSearches);
    setPeriodVenueViews(totalViews);
    setVenueSearchTrend(calcTrend(totalSearches, prevSearches));
    setVenueViewTrend(calcTrend(totalViews, prevViews));

    // Time series
    const ts: TimeSeriesPoint[] = Object.entries(dailyMap).map(([dateStr, counts]) => ({
      date: new Date(dateStr).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' }),
      venueSearches: counts.searches,
      venueViews: counts.views,
      offerViews: 0,
      offerRedemptions: 0,
    }));
    setVenueTimeSeries(ts);

    // Hourly
    setHourlySearchData(Array.from({ length: 24 }, (_, i) => ({
      hour: `${i.toString().padStart(2, '0')}:00`,
      searches: hourlyS[i],
    })));

    // Day of week
    setDayOfWeekData(DAYS.map((day, i) => ({
      day,
      searches: dowS[i],
      views: dowV[i],
    })));

    // Peak hour
    const peakIdx = hourlyS.indexOf(Math.max(...hourlyS));
    if (Math.max(...hourlyS) > 0) {
      const fmt = (h: number) => h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;
      setPeakHourRange(`${fmt(Math.max(0, peakIdx - 1))} – ${fmt(Math.min(23, peakIdx + 1))}`);
    }

    // Weekend lift
    const avgWeekday = [1, 2, 3, 4, 5].map(i => dowV[i]).reduce((s, v) => s + v, 0) / 5;
    const avgWeekend = [0, 6].map(i => dowV[i]).reduce((s, v) => s + v, 0) / 2;
    setWeekendLift(avgWeekday > 0 ? parseFloat((((avgWeekend - avgWeekday) / avgWeekday) * 100).toFixed(0)) : 0);
    setPeakDayOfWeek(Math.max(...dowV) > 0 ? DAYS[dowV.indexOf(Math.max(...dowV))] : '');

    // Venue-level performance
    const vPerf: VenuePerformance[] = filteredVenues.map(venue => {
      let searches = 0, views = 0;
      currentSnaps.forEach((snap, idx) => {
        if (venueIds[idx] === venue.id) {
          snap.docs.forEach(doc => {
            searches += doc.data().searches || 0;
            views += doc.data().views || 0;
          });
        }
      });
      return {
        venueId: venue.id,
        venueName: venue.name || venue.businessName || 'Unnamed',
        searches,
        views,
        conversionRate: views > 0 ? (searches / views) * 100 : 0,
      };
    }).sort((a, b) => b.views - a.views);
    setVenuePerformance(vPerf);
  };

  const loadOfferAnalytics = async () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - timeRange);

    const prevEndDate = new Date(startDate);
    prevEndDate.setDate(prevEndDate.getDate() - 1);
    const prevStartDate = new Date(prevEndDate);
    prevStartDate.setDate(prevStartDate.getDate() - timeRange);

    const filteredOffers = offers.filter(o => o.ownerId === adminId);

    // Always build the leaderboard from offer docs (lifetime totals)
    const leaderboard = filteredOffers.map(o => ({
      id: o.id,
      title: o.title,
      type: o.discountType,
      views: o.analytics?.views || 0,
      redemptions: o.analytics?.redemptions || 0,
      periodViews: 0,
      periodRedemptions: 0,
      conversionRate: (o.analytics?.views || 0) > 0
        ? ((o.analytics?.redemptions || 0) / (o.analytics?.views || 0)) * 100 : 0,
      venueName: o.venueName,
    })).sort((a, b) => b.redemptions - a.redemptions);

    if (!filteredOffers.length) {
      setOfferPerformance(leaderboard);
      return;
    }

    const startTs = Timestamp.fromDate(startDate);
    const endTs = Timestamp.fromDate(endDate);
    const prevStartTs = Timestamp.fromDate(prevStartDate);
    const prevEndTs = Timestamp.fromDate(prevEndDate);

    console.log('loadOfferAnalytics — adminId:', adminId, '| offers:', filteredOffers.length, '| from:', startDate.toLocaleDateString(), '| to:', endDate.toLocaleDateString());

    // Clean single queries using ownerId — no chunking needed
    const [viewSnap, redemptionSnap, prevViewSnap, prevRedemptionSnap] = await Promise.all([
      getDocs(query(
        collection(db, 'offer_views'),
        where('ownerId', '==', adminId),
        where('viewed_at', '>=', startTs),
        where('viewed_at', '<=', endTs),
      )).then(r => { console.log('offer_views current: ok', r.size); return r; })
        .catch(e => { console.error('offer_views current FAILED:', e.code, e.message); throw e; }),
      getDocs(query(
        collection(db, 'offer_redemptions'),
        where('ownerId', '==', adminId),
        where('redeemedAt', '>=', startTs),
        where('redeemedAt', '<=', endTs),
      )).then(r => { console.log('offer_redemptions current: ok', r.size); return r; })
        .catch(e => { console.error('offer_redemptions current FAILED:', e.code, e.message); throw e; }),
      getDocs(query(
        collection(db, 'offer_views'),
        where('ownerId', '==', adminId),
        where('viewed_at', '>=', prevStartTs),
        where('viewed_at', '<=', prevEndTs),
      )).then(r => { console.log('offer_views prev: ok', r.size); return r; })
        .catch(e => { console.error('offer_views prev FAILED:', e.code, e.message); throw e; }),
      getDocs(query(
        collection(db, 'offer_redemptions'),
        where('ownerId', '==', adminId),
        where('redeemedAt', '>=', prevStartTs),
        where('redeemedAt', '<=', prevEndTs),
      )).then(r => { console.log('offer_redemptions prev: ok', r.size); return r; })
        .catch(e => { console.error('offer_redemptions prev FAILED:', e.code, e.message); throw e; }),
    ]);

    // Bucket events by date for time-series
    const dailyMap: Record<string, { offerViews: number; offerRedemptions: number }> = {};
    for (let i = timeRange; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      dailyMap[d.toISOString().substring(0, 10)] = { offerViews: 0, offerRedemptions: 0 };
    }

    let totalViews = 0, totalRedemptions = 0;
    const perOfferViews: Record<string, number> = {};
    const perOfferRedemptions: Record<string, number> = {};

    viewSnap.docs.forEach(doc => {
      const d = doc.data();
      const dateStr = d.viewed_at.toDate().toISOString().substring(0, 10);
      if (dailyMap[dateStr]) dailyMap[dateStr].offerViews++;
      totalViews++;
      perOfferViews[d.offer_id] = (perOfferViews[d.offer_id] || 0) + 1;
    });

    redemptionSnap.docs.forEach(doc => {
      const d = doc.data();
      const dateStr = d.redeemedAt.toDate().toISOString().substring(0, 10);
      if (dailyMap[dateStr]) dailyMap[dateStr].offerRedemptions++;
      totalRedemptions++;
      perOfferRedemptions[d.offerId] = (perOfferRedemptions[d.offerId] || 0) + 1;
    });

    // Previous period totals for trend calculation
    const prevViews = prevViewSnap.size;
    const prevRedemptions = prevRedemptionSnap.size;

    const calcTrend = (curr: number, prev: number) =>
      prev === 0 ? null : parseFloat((((curr - prev) / prev) * 100).toFixed(1));

    setPeriodOfferViews(totalViews);
    setPeriodOfferRedemptions(totalRedemptions);
    setOfferViewTrend(calcTrend(totalViews, prevViews));
    setOfferRedemptionTrend(calcTrend(totalRedemptions, prevRedemptions));
    setOverallConversionRate(totalViews > 0 ? (totalRedemptions / totalViews) * 100 : 0);

    // Time series
    const ts: TimeSeriesPoint[] = Object.entries(dailyMap).map(([dateStr, counts]) => ({
      date: new Date(dateStr).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' }),
      venueSearches: 0,
      venueViews: 0,
      offerViews: counts.offerViews,
      offerRedemptions: counts.offerRedemptions,
    }));
    setOfferTimeSeries(ts);

    // Merge period event counts into the leaderboard
    const perf = leaderboard.map(o => ({
      ...o,
      periodViews: perOfferViews[o.id] || 0,
      periodRedemptions: perOfferRedemptions[o.id] || 0,
    }));
    setOfferPerformance(perf);
  };

  // ─── Time range handler ───────────────────────────────────────────────────

  const handleTimeRangeChange = (_: any, val: TimeRange | null) => {
    if (val !== null) setTimeRange(val);
  };

  // ─── Loading / error states ───────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={40} sx={{ color: '#6c63ff' }} />
      </Box>
    );
  }

  // ─── Shared toggle ────────────────────────────────────────────────────────

  const TimeRangeToggle = () => (
    <ToggleButtonGroup
      value={timeRange}
      exclusive
      onChange={handleTimeRangeChange}
      size="small"
      sx={{
        '& .MuiToggleButton-root': {
          border: '1px solid #e0e3ea', borderRadius: 1.5, px: 2, py: 0.6,
          fontSize: '0.78rem', fontWeight: 500, color: '#8a94a6', textTransform: 'none',
          '&.Mui-selected': { background: '#6c63ff', color: '#fff', borderColor: '#6c63ff', '&:hover': { background: '#5b52e0' } },
        },
      }}
    >
      <ToggleButton value={7}>7 days</ToggleButton>
      <ToggleButton value={30}>30 days</ToggleButton>
      <ToggleButton value={60}>60 days</ToggleButton>
    </ToggleButtonGroup>
  );

  // ─── Venue Analytics Tab ──────────────────────────────────────────────────

  const VenueAnalyticsTab = () => (
    <Box>
      {/* Stat cards */}
      <Grid container spacing={2.5} mb={4}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Venue Searches" value={periodVenueSearches.toLocaleString()}
            icon={<Search />} accent="#6c63ff" bgTint="#f5f4ff"
            trend={venueSearchTrend} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Venue Views" value={periodVenueViews.toLocaleString()}
            icon={<Visibility />} accent="#f59e0b" bgTint="#fffbf0"
            trend={venueViewTrend} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Peak Day" value={peakDayOfWeek || '--'}
            icon={<Schedule />} accent="#16a76a" bgTint="#f0faf5"
            sub="Highest search activity" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard label="Weekend Lift"
            value={weekendLift === 0 ? 'Even' : `${weekendLift > 0 ? '+' : ''}${weekendLift}%`}
            icon={<People />} accent="#e040fb" bgTint="#fdf4ff"
            sub="vs weekday average" />
        </Grid>
      </Grid>

      {/* Searches + views over time */}
      <Card sx={{ mb: 3, borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
            <SectionHeader title="Venue Searches & Views Over Time" subtitle="How customers are finding your venues" />
            <Box display="flex" gap={2}>
              <LegendDot color="#6c63ff" label="Searches" />
              <LegendDot color="#f59e0b" label="Views" />
            </Box>
          </Box>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={venueTimeSeries}>
              <defs>
                <linearGradient id="gVS" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6c63ff" stopOpacity={0.15} /><stop offset="100%" stopColor="#6c63ff" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gVV" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.15} /><stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...GRID_STYLE} vertical={false} />
              <XAxis dataKey="date" {...AXIS_STYLE} tickFormatter={(v, i) => i % Math.ceil(venueTimeSeries.length / 8) === 0 ? v : ''} />
              <YAxis {...AXIS_STYLE} width={36} />
              <RechartsTooltip {...TOOLTIP_STYLE} />
              <Area type="monotone" dataKey="venueSearches" stroke="#6c63ff" strokeWidth={2} fill="url(#gVS)" dot={false} name="Searches" />
              <Area type="monotone" dataKey="venueViews" stroke="#f59e0b" strokeWidth={2} fill="url(#gVV)" dot={false} name="Views" />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Per-venue breakdown */}
      <Grid container spacing={2.5} mb={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader title="Searches per Venue" subtitle="Total venue-level searches this period" />
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={venuePerformance.slice(0, 6)} layout="vertical" barSize={13}>
                  <CartesianGrid {...GRID_STYLE} horizontal={false} />
                  <XAxis type="number" {...AXIS_STYLE} />
                  <YAxis dataKey="venueName" type="category" width={110} {...AXIS_STYLE} tick={{ ...AXIS_STYLE.tick, fontSize: 11 }} />
                  <RechartsTooltip {...TOOLTIP_STYLE} />
                  <Bar dataKey="searches" name="Searches" radius={[0, 5, 5, 0]}>
                    {venuePerformance.slice(0, 6).map((_, i) => (
                      <Cell key={i} fill={i === 0 ? '#6c63ff' : `rgba(108,99,255,${0.65 - i * 0.1})`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader title="Views per Venue" subtitle="Total venue profile views this period" />
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={venuePerformance.slice(0, 6)} layout="vertical" barSize={13}>
                  <CartesianGrid {...GRID_STYLE} horizontal={false} />
                  <XAxis type="number" {...AXIS_STYLE} />
                  <YAxis dataKey="venueName" type="category" width={110} {...AXIS_STYLE} tick={{ ...AXIS_STYLE.tick, fontSize: 11 }} />
                  <RechartsTooltip {...TOOLTIP_STYLE} />
                  <Bar dataKey="views" name="Views" radius={[0, 5, 5, 0]}>
                    {venuePerformance.slice(0, 6).map((_, i) => (
                      <Cell key={i} fill={i === 0 ? '#f59e0b' : `rgba(245,158,11,${0.65 - i * 0.1})`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Hourly + day of week */}
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader title="Search Activity by Hour" subtitle="When customers are looking for your venues" />
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={hourlySearchData}>
                  <defs>
                    <linearGradient id="gHSearch" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6c63ff" stopOpacity={0.15} /><stop offset="100%" stopColor="#6c63ff" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...GRID_STYLE} vertical={false} />
                  <XAxis dataKey="hour" {...AXIS_STYLE} tickFormatter={(v, i) => i % 4 === 0 ? v.replace(':00', '') : ''} />
                  <YAxis {...AXIS_STYLE} width={30} />
                  <RechartsTooltip {...TOOLTIP_STYLE} />
                  <Area type="monotone" dataKey="searches" stroke="#6c63ff" strokeWidth={2} fill="url(#gHSearch)" dot={false} name="Searches" />
                </AreaChart>
              </ResponsiveContainer>
              {peakHourRange && (
                <Box mt={2} px={2} py={1.25} sx={{ background: '#f5f4ff', borderRadius: 1.5, border: '1px solid #e0dbff' }}>
                  <Typography sx={{ fontSize: '0.78rem', color: '#6c63ff', fontWeight: 600 }}>Peak: {peakHourRange}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <SectionHeader title="Day of Week Activity" subtitle="Busiest days for your venues" />
                <Box display="flex" gap={1.5}>
                  <LegendDot color="#6c63ff" label="Searches" />
                  <LegendDot color="#f59e0b" label="Views" />
                </Box>
              </Box>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={dayOfWeekData} barGap={3} barCategoryGap="28%">
                  <CartesianGrid {...GRID_STYLE} vertical={false} />
                  <XAxis dataKey="day" {...AXIS_STYLE} tickFormatter={v => v.slice(0, 3)} />
                  <YAxis {...AXIS_STYLE} width={30} />
                  <RechartsTooltip {...TOOLTIP_STYLE} />
                  <Bar dataKey="searches" fill="#6c63ff" name="Searches" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="views" fill="#f59e0b" name="Views" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              {(weekendLift !== 0 || peakDayOfWeek) && (
                <Box mt={2} px={2} py={1.25} sx={{ background: '#eff6ff', borderRadius: 1.5, border: '1px solid #bfdbfe' }}>
                  <Typography sx={{ fontSize: '0.78rem', color: '#3b82f6', fontWeight: 600 }}>
                    {weekendLift > 0
                      ? `Weekend activity ${weekendLift}% higher than weekdays`
                      : weekendLift < 0
                      ? `Weekday activity ${Math.abs(weekendLift)}% higher than weekends`
                      : 'Even spread across the week'}
                    {peakDayOfWeek ? ` — ${peakDayOfWeek} is your busiest day` : ''}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // ─── Offer Analytics Tab ──────────────────────────────────────────────────

  const OfferAnalyticsTab = () => {
    const maxRedemptions = Math.max(...offerPerformance.map(o => o.redemptions), 1);

    // Offer type breakdown
    const typeBreakdown = Object.entries(
      offerPerformance.reduce((acc, o) => {
        if (!acc[o.type]) acc[o.type] = { redemptions: 0, views: 0 };
        acc[o.type].redemptions += o.redemptions;
        acc[o.type].views += o.views;
        return acc;
      }, {} as Record<string, { redemptions: number; views: number }>)
    ).map(([name, data]) => ({ name: name.toUpperCase(), value: data.redemptions, ...data }));

    return (
      <Box>
        {/* Stat cards */}
        <Grid container spacing={2.5} mb={4}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard label="Offer Views" value={periodOfferViews.toLocaleString()}
              icon={<Visibility />} accent="#6c63ff" bgTint="#f5f4ff"
              trend={offerViewTrend} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard label="Redemptions" value={periodOfferRedemptions.toLocaleString()}
              icon={<LocalOffer />} accent="#16a76a" bgTint="#f0faf5"
              trend={offerRedemptionTrend} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard label="Conversion Rate" value={`${overallConversionRate.toFixed(1)}%`}
              icon={<TrendingUp />} accent="#f59e0b" bgTint="#fffbf0"
              sub="Views that became redemptions" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard label="Active Offers" value={offers.filter(o => o.isActive && o.status === 'active').length}
              icon={<EmojiEvents />} accent="#e040fb" bgTint="#fdf4ff"
              sub={`${offers.length} total`} />
          </Grid>
        </Grid>

        {/* Offer views + redemptions over time */}
        <Card sx={{ mb: 3, borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
              <SectionHeader title="Offer Views & Redemptions Over Time" subtitle={`Event-level data for the last ${timeRange} days`} />
              <Box display="flex" gap={2}>
                <LegendDot color="#6c63ff" label="Views" />
                <LegendDot color="#16a76a" label="Redemptions" />
              </Box>
            </Box>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={offerTimeSeries}>
                <defs>
                  <linearGradient id="gOV" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6c63ff" stopOpacity={0.15} /><stop offset="100%" stopColor="#6c63ff" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gOR" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#16a76a" stopOpacity={0.15} /><stop offset="100%" stopColor="#16a76a" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...GRID_STYLE} vertical={false} />
                <XAxis dataKey="date" {...AXIS_STYLE} tickFormatter={(v, i) => i % Math.ceil(offerTimeSeries.length / 8) === 0 ? v : ''} />
                <YAxis {...AXIS_STYLE} width={36} />
                <RechartsTooltip {...TOOLTIP_STYLE} />
                <Area type="monotone" dataKey="offerViews" stroke="#6c63ff" strokeWidth={2} fill="url(#gOV)" dot={false} name="Views" />
                <Area type="monotone" dataKey="offerRedemptions" stroke="#16a76a" strokeWidth={2} fill="url(#gOR)" dot={false} name="Redemptions" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Leaderboard + type breakdown */}
        <Grid container spacing={2.5} mb={3}>
          {/* Leaderboard */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Card sx={{ borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <EmojiEvents sx={{ color: '#f59e0b', fontSize: 20 }} />
                  <Typography variant="h6" fontWeight={600} color="#1a1d23">Offer Leaderboard</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2.5}>Ranked by lifetime redemptions</Typography>
                <Box display="flex" flexDirection="column" gap={1.25}>
                  {offerPerformance.slice(0, 10).map((offer: any, index) => (
                    <Box key={offer.id} sx={{ p: 1.75, borderRadius: 2, background: '#f8f9fc', border: '1px solid #eef0f5' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Box display="flex" alignItems="center" gap={1.25} flex={1} minWidth={0}>
                          <Box sx={{
                            width: 22, height: 22, borderRadius: 1, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', flexShrink: 0,
                            background: index < 3 ? '#fff8ec' : '#f0f0f5',
                            color: index < 3 ? '#f59e0b' : '#8a94a6',
                            fontSize: '0.68rem', fontWeight: 700,
                            border: index < 3 ? '1px solid #ffe0a0' : '1px solid #e8eaf0',
                          }}>
                            {index + 1}
                          </Box>
                          <Box minWidth={0}>
                            <Typography variant="body2" fontWeight={600} color="#1a1d23" noWrap>{offer.title}</Typography>
                            <Typography variant="caption" color="text.secondary">{offer.venueName}</Typography>
                          </Box>
                        </Box>
                        <Box display="flex" gap={2} alignItems="center" flexShrink={0} ml={1}>
                          <Box textAlign="right">
                            <Typography sx={{ fontSize: '0.68rem', color: '#8a94a6' }}>Views</Typography>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#6c63ff' }}>{offer.views.toLocaleString()}</Typography>
                          </Box>
                          <Box textAlign="right">
                            <Typography sx={{ fontSize: '0.68rem', color: '#8a94a6' }}>Redeemed</Typography>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#16a76a' }}>{offer.redemptions.toLocaleString()}</Typography>
                          </Box>
                          {offer.periodRedemptions > 0 && (
                            <Box textAlign="right">
                              <Typography sx={{ fontSize: '0.68rem', color: '#8a94a6' }}>This period</Typography>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#f59e0b' }}>+{offer.periodRedemptions}</Typography>
                            </Box>
                          )}
                          <Box sx={{
                            px: 1, py: 0.4, borderRadius: 1,
                            background: offer.conversionRate > 20 ? '#f0faf5' : '#f8f9fc',
                            border: `1px solid ${offer.conversionRate > 20 ? '#bbf0d9' : '#e8eaf0'}`,
                          }}>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: offer.conversionRate > 20 ? '#16a76a' : '#8a94a6' }}>
                              {offer.conversionRate.toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ height: 4, borderRadius: 2, background: '#e8eaf0', overflow: 'hidden' }}>
                        <Box sx={{
                          height: '100%', borderRadius: 2,
                          width: `${(offer.redemptions / maxRedemptions) * 100}%`,
                          background: index < 3 ? 'linear-gradient(90deg, #f59e0b, #fbbf24)' : '#6c63ff',
                          transition: 'width 0.6s ease',
                        }} />
                      </Box>
                    </Box>
                  ))}
                  {offerPerformance.length === 0 && (
                    <Box textAlign="center" py={4}>
                      <LocalOffer sx={{ fontSize: 40, color: '#d1d5e0', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">No offer data yet</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Type breakdown donut */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <Card sx={{ borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <CardContent sx={{ p: 3 }}>
                <SectionHeader title="Performance by Type" subtitle="Which discount types drive redemptions" />
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={typeBreakdown} cx="50%" cy="50%" innerRadius={55} outerRadius={88} paddingAngle={3} dataKey="value" labelLine={false}>
                      {typeBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <RechartsTooltip {...TOOLTIP_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
                <Divider sx={{ my: 2, borderColor: '#eef0f5' }} />
                <Box display="flex" flexDirection="column" gap={1}>
                  {typeBreakdown.map((item, i) => {
                    const conv = item.views > 0 ? (item.redemptions / item.views) * 100 : 0;
                    return (
                      <Box key={item.name} display="flex" justifyContent="space-between" alignItems="center"
                        sx={{ p: 1.25, borderRadius: 1.5, background: '#f8f9fc', border: '1px solid #eef0f5' }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                          <Typography variant="body2" fontWeight={600} color="#1a1d23">{item.name}</Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="body2" fontWeight={600} color="#16a76a">{item.redemptions} redeemed</Typography>
                          <Typography variant="caption" color="text.secondary">{conv.toFixed(1)}% conversion</Typography>
                        </Box>
                      </Box>
                    );
                  })}
                  {typeBreakdown.length === 0 && (
                    <Typography variant="body2" color="text.secondary" textAlign="center">No offer type data yet</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Insights */}
        <Card sx={{ borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} color="#1a1d23" gutterBottom>Insights & Recommendations</Typography>
            <Grid container spacing={2} mt={0.5}>
              {[
                {
                  icon: '✓', title: 'Conversion Performance', bg: '#f0faf5', border: '#bbf0d9', titleColor: '#16a76a',
                  body: overallConversionRate > 0
                    ? `Your conversion rate is ${overallConversionRate.toFixed(1)}%${overallConversionRate >= 12 ? ', above the 12% industry average. Keep creating engaging offers!' : '. The industry average is 12% — try shorter validity windows or higher-value discounts.'}`
                    : 'Not enough data yet. Get your offers in front of customers to start tracking conversions.',
                },
                {
                  icon: '⚡', title: 'Top Offer Insight', bg: '#fff8ec', border: '#ffe0a0', titleColor: '#f59e0b',
                  body: offerPerformance.length > 0
                    ? `"${(offerPerformance[0] as any).title}" is your best performer with ${offerPerformance[0].redemptions} redemptions. ${offerPerformance[0].conversionRate > 15 ? 'Consider creating similar offers to replicate this success.' : 'Boosting its visibility could drive even more redemptions.'}`
                    : 'Create your first offer to start tracking performance.',
                },
              ].map((insight) => (
                <Grid key={insight.title} size={{ xs: 12, md: 6 }}>
                  <Box sx={{ p: 2, borderRadius: 2, background: insight.bg, border: `1px solid ${insight.border}` }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: insight.titleColor, mb: 0.75 }}>
                      {insight.icon} {insight.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{insight.body}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  };

  // ─── Main render ──────────────────────────────────────────────────────────

  return (
    <Box sx={{ background: '#f8f9fc' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700} color="#1a1d23">Performance Analytics</Typography>
        <TimeRangeToggle />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {/* Tab switcher */}
      <Box sx={{ mb: 3, borderBottom: '1px solid #e8eaf0' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            '& .MuiTab-root': {
              fontSize: '0.82rem', fontWeight: 500, textTransform: 'none', minHeight: 44,
              color: '#8a94a6', px: 2.5,
              '&.Mui-selected': { color: '#1a1d23', fontWeight: 600 },
            },
            '& .MuiTabs-indicator': { height: 2, borderRadius: '2px 2px 0 0', background: 'linear-gradient(90deg, #6c63ff, #e040fb)' },
          }}
        >
          <Tab label="Venue Analytics" icon={<Business sx={{ fontSize: 16 }} />} iconPosition="start" />
          <Tab label="Offer Analytics" icon={<LocalOffer sx={{ fontSize: 16 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      {activeTab === 0 && <VenueAnalyticsTab />}
      {activeTab === 1 && <OfferAnalyticsTab />}
    </Box>
  );
};

export default VenueAdminAnalytics;